import { API_BASE_URL } from "../config.js";
import { translations } from "../utils/translations.js";
import { formatProductSize } from "../utils/format.js";

function getStoredToken() {
  try {
    return localStorage.getItem("tl_token");
  } catch {
    return null;
  }
}

export function getCurrentLanguage() {
  try {
    const lang = localStorage.getItem("tl_storefront_lang");
    return lang === "en" ? "en" : "ar";
  } catch {
    return "ar";
  }
}

const AR_COLOR_MAP = {
  white: "أبيض",
  black: "أسود",
  grey: "رمادي",
  gray: "رمادي",
  navy: "كحلي",
  "navy blue": "كحلي",
  blue: "أزرق",
  "light blue": "أزرق فاتح",
  red: "أحمر",
  green: "أخضر",
  olive: "زيتي",
  "olive green": "زيتي",
  yellow: "أصفر",
  pink: "وردي",
  "blush pink": "وردي هادئ",
  beige: "بيج",
  burgundy: "نبيتي",
  charcoal: "فحمي",
  brown: "بني",
  ivory: "عاجي",
  sand: "رملي",
  "sand dune": "رملي",
  gold: "ذهبي",
  silver: "فضي",
  purple: "بنفسجي",
  orange: "برتقالي",
  teal: "تركواز",
  mint: "نعناعي",
  "mint green": "نعناعي",
};

/**
 * Parses and translates dynamic backend English error messages into informative Arabic.
 */
function tryTranslateDynamicPatternToArabic(msg) {
  if (!msg || typeof msg !== "string") return null;

  // 1. Out of stock with variant:
  // e.g. "kids bathrobe of size S with color yellow is out of stock!"
  // or   "kids bathrobe (Size: S, Color: yellow) is out of stock!"
  const outOfStockVariantMatch = msg.match(
    /^(.+?)(?:\s+of size\s+(\S+)\s+with color\s+(\S+)|\s*\(Size:\s*([^,]+),\s*Color:\s*([^)]+)\))\s+is out of stock!?$/i
  );
  if (outOfStockVariantMatch) {
    const product = outOfStockVariantMatch[1].trim();
    const rawSize = (outOfStockVariantMatch[2] || outOfStockVariantMatch[4] || "").trim();
    const rawColor = (outOfStockVariantMatch[3] || outOfStockVariantMatch[5] || "").trim();
    const translatedSize = formatProductSize(rawSize, "ar") || rawSize;
    const translatedColor = AR_COLOR_MAP[rawColor.toLowerCase()] || rawColor;

    const details = [];
    if (translatedSize && translatedSize !== "None" && translatedSize !== "N/A") {
      details.push(`المقاس: ${translatedSize}`);
    }
    if (translatedColor && translatedColor !== "N/A") {
      details.push(`اللون: ${translatedColor}`);
    }

    const variantInfo = details.length > 0 ? ` (${details.join("، ")})` : "";
    return `المنتج "${product}"${variantInfo} غير متوفر في المخزون حالياً!`;
  }

  // 2. Single product out of stock (no variant specified):
  const outOfStockSimpleMatch = msg.match(/^(.+?)\s+is out of stock!?$/i);
  if (outOfStockSimpleMatch) {
    return `المنتج "${outOfStockSimpleMatch[1].trim()}" غير متوفر في المخزون حالياً!`;
  }

  // 3. Variant no longer available:
  // e.g. "kids bathrobe of size S with color yellow is no longer available for purchase."
  const unavailableVariantMatch = msg.match(
    /^(.+?)(?:\s+of size\s+(\S+)\s+with color\s+(\S+)|\s*\(Size:\s*([^,]+),\s*Color:\s*([^)]+)\))\s+is no longer available/i
  );
  if (unavailableVariantMatch) {
    const product = unavailableVariantMatch[1].trim();
    const rawSize = (unavailableVariantMatch[2] || unavailableVariantMatch[4] || "").trim();
    const rawColor = (unavailableVariantMatch[3] || unavailableVariantMatch[5] || "").trim();
    const translatedSize = formatProductSize(rawSize, "ar") || rawSize;
    const translatedColor = AR_COLOR_MAP[rawColor.toLowerCase()] || rawColor;

    const details = [];
    if (translatedSize && translatedSize !== "None" && translatedSize !== "N/A") {
      details.push(`المقاس: ${translatedSize}`);
    }
    if (translatedColor && translatedColor !== "N/A") {
      details.push(`اللون: ${translatedColor}`);
    }

    const variantInfo = details.length > 0 ? ` (${details.join("، ")})` : "";
    return `المنتج "${product}"${variantInfo} لم يعد متاحاً للشراء.`;
  }

  // 4. Single product unavailable:
  const unavailableSimpleMatch = msg.match(/^(.+?)\s+is no longer available/i);
  if (unavailableSimpleMatch) {
    return `المنتج "${unavailableSimpleMatch[1].trim()}" لم يعد متاحاً للشراء.`;
  }

  // 5. Coupon patterns:
  const couponExpiredMatch = msg.match(/Coupon '([^']+)' has expired/i);
  if (couponExpiredMatch) {
    return `انتهت صلاحية كود الخصم "${couponExpiredMatch[1]}".`;
  }

  const couponInactiveMatch = msg.match(/Coupon '([^']+)' is currently inactive/i);
  if (couponInactiveMatch) {
    return `كود الخصم "${couponInactiveMatch[1]}" غير مفعّل حالياً.`;
  }

  const couponNotActiveYetMatch = msg.match(/Coupon '([^']+)' is not active yet/i);
  if (couponNotActiveYetMatch) {
    return `كود الخصم "${couponNotActiveYetMatch[1]}" ليس مفعّلاً بعد.`;
  }

  const couponMinSpendMatch = msg.match(
    /Order amount \(([^)]+)\) does not meet the minimum spend requirement of ([^ ]+) for coupon '([^']+)'/i
  );
  if (couponMinSpendMatch) {
    return `قيمة الطلب (${couponMinSpendMatch[1]} ج.م) لا تحقق الحد الأدنى (${couponMinSpendMatch[2]} ج.م) المطلوب لتفعيل كوبون "${couponMinSpendMatch[3]}".`;
  }

  const couponMaxLimitMatch = msg.match(/Coupon '([^']+)' has reached its maximum global usage limit/i);
  if (couponMaxLimitMatch) {
    return `تم استنفاد الحد الأقصى الكلي لاستخدام كود الخصم "${couponMaxLimitMatch[1]}".`;
  }

  const couponUserLimitMatch = msg.match(
    /You have reached the maximum allowed redemptions for coupon '([^']+)'/i
  );
  if (couponUserLimitMatch) {
    return `لقد استنفدت الحد المسموح لك لاستخدام كود الخصم "${couponUserLimitMatch[1]}".`;
  }

  // 6. Common order & purchase rules:
  if (/Exchange is only available for Completed or Returned orders/i.test(msg)) {
    return "الاستبدال متاح فقط للطلبات المكتملة أو المرتجعة.";
  }
  if (/Cannot refund item after 10 days from purchase/i.test(msg)) {
    return "لا يمكن استرداد المبلغ بعد مرور 10 أيام من تاريخ الشراء.";
  }
  if (/Maximum 10 items of same product/i.test(msg)) {
    return "الحد الأقصى المسموح به هو 10 قطع من نفس المنتج.";
  }

  return null;
}

// Broad category codes that should never override informative dynamic error messages
const GENERIC_CATEGORY_CODES = new Set([
  "DOMAIN_RULE_VIOLATION",
  "BAD_REQUEST",
  "GENERIC_ERROR",
  "INTERNAL_SERVER_ERROR",
]);

/**
 * Resolves a human-friendly, localized error message:
 * 1. Honors server-provided Arabic messages with dynamic details.
 * 2. Parses and translates dynamic patterns (out-of-stock, variant availability, coupons).
 * 3. Matches specific error codes (excluding broad categories like DOMAIN_RULE_VIOLATION).
 * 4. Resolves validation error dictionaries.
 * 5. Uses exact string translation mappings.
 * 6. Falls back safely based on language.
 */
export function resolveApiErrorMessage(data, status, currentLang = getCurrentLanguage()) {
  const dict = translations[currentLang] || translations.ar;

  // Extract raw error message text
  const rawMsg =
    typeof data === "string"
      ? data
      : data?.error || data?.message || data?.title || null;

  // 1. If backend already sent an Arabic message (contains Arabic characters), ALWAYS display it directly
  if (rawMsg && /[\u0600-\u06FF]/.test(rawMsg)) {
    return rawMsg;
  }

  // 2. If user is in Arabic and backend returned dynamic English content, try dynamic pattern translation
  if (currentLang === "ar" && rawMsg) {
    const dynamicArabicTranslation = tryTranslateDynamicPatternToArabic(rawMsg);
    if (dynamicArabicTranslation) {
      return dynamicArabicTranslation;
    }
  }

  // 3. Check for specific machine-readable error codes (ignoring broad category codes)
  if (data?.code && !GENERIC_CATEGORY_CODES.has(data.code) && dict[`errors.${data.code}`]) {
    // If the code is OUT_OF_STOCK and rawMsg is present and in English mode, show rawMsg; otherwise translated
    if (data.code === "OUT_OF_STOCK" && currentLang === "en" && rawMsg) {
      return rawMsg;
    }
    return dict[`errors.${data.code}`];
  }

  // 4. Exact string translation mapping (for static English backend strings)
  if (rawMsg) {
    if (dict[`errors.messages.${rawMsg}`]) {
      return dict[`errors.messages.${rawMsg}`];
    }
    if (dict[`errors.${rawMsg}`]) {
      return dict[`errors.${rawMsg}`];
    }
  }

  // 5. Validation errors dictionary
  if (data?.errors && typeof data.errors === "object") {
    const fieldErrorMessages = [];
    for (const key in data.errors) {
      const fieldErrors = data.errors[key];
      const errorList = Array.isArray(fieldErrors) ? fieldErrors : [fieldErrors];

      for (const errItem of errorList) {
        if (!errItem) continue;
        if (dict[`errors.messages.${errItem}`]) {
          fieldErrorMessages.push(dict[`errors.messages.${errItem}`]);
        } else if (dict[`errors.${errItem}`]) {
          fieldErrorMessages.push(dict[`errors.${errItem}`]);
        } else if (currentLang === "en") {
          fieldErrorMessages.push(errItem);
        }
      }
    }

    if (fieldErrorMessages.length > 0) {
      return fieldErrorMessages.join("\n");
    }

    if (currentLang === "ar") {
      return dict["errors.VALIDATION_FAILED"] || "يرجى التحقق من صحة البيانات المدخلة والمحاولة مجدداً.";
    }
  }

  // 6. In English mode, display the backend's message
  if (currentLang === "en" && rawMsg) {
    return rawMsg;
  }

  // 7. HTTP Status Code fallbacks
  if (status === 401 && dict["errors.UNAUTHORIZED"]) {
    return dict["errors.UNAUTHORIZED"];
  }
  if (status === 403 && dict["errors.FORBIDDEN"]) {
    return dict["errors.FORBIDDEN"];
  }
  if (status === 404 && dict["errors.RESOURCE_NOT_FOUND"]) {
    return dict["errors.RESOURCE_NOT_FOUND"];
  }
  if (status >= 500 && dict["errors.INTERNAL_SERVER_ERROR"]) {
    return dict["errors.INTERNAL_SERVER_ERROR"];
  }

  // 8. Final fallback
  if (currentLang === "ar") {
    return dict["errors.GENERIC_ERROR"] || "حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى لاحقاً.";
  }
  return rawMsg || dict["errors.GENERIC_ERROR"] || "An unexpected error occurred. Please try again.";
}

export async function apiFetch(path, options = {}) {
  const currentLang = getCurrentLanguage();
  const token =
    Object.prototype.hasOwnProperty.call(options, "token")
      ? options.token
      : getStoredToken();

  const headers = {
    ...(options.body && !(options.body instanceof FormData)
      ? { "Content-Type": "application/json" }
      : {}),
    "Accept-Language": currentLang,
    ...options.headers,
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const url = path.startsWith("http") ? path : `${API_BASE_URL}${path}`;

  let res;
  try {
    res = await fetch(url, { ...options, headers });
  } catch (networkErr) {
    const dict = translations[currentLang] || translations.ar;
    const err = new Error(dict["errors.NETWORK_ERROR"] || "Network error. Please check your internet connection.");
    err.code = "NETWORK_ERROR";
    err.status = 0;
    err.originalError = networkErr;
    throw err;
  }

  if (res.status === 204) return null;

  const contentType = res.headers.get("content-type") || "";
  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  // If server returned an HTML document (e.g. hitting frontend SPA router or Cloudflare error)
  if (contentType.includes("text/html") || (typeof data === "string" && data.trim().startsWith("<"))) {
    const dict = translations[currentLang] || translations.ar;
    const errorMsg = !res.ok
      ? (dict["errors.INTERNAL_SERVER_ERROR"] || `Server returned HTML error (${res.status} ${res.statusText})`)
      : `Received HTML response instead of JSON. Ensure the backend API server is running.`;
    const err = new Error(errorMsg);
    err.status = res.status;
    err.body = data;
    throw err;
  }

  if (!res.ok) {
    const localizedMessage = resolveApiErrorMessage(data, res.status, currentLang);
    const err = new Error(localizedMessage);
    err.code = data?.code || null;
    err.rawMessage = typeof data === "string" ? data : (data?.error || data?.message || data?.title || res.statusText);
    err.status = res.status;
    err.body = data;
    throw err;
  }

  return data;
}
